import { Heading, Text } from '@chakra-ui/react';
import { NextSeo } from 'next-seo';
import { Fragment } from 'react';

import { Content } from '@/components/templatePage/content';
import { Header } from '@/components/templatePage/header';
import { Layout } from '@/components/ui/shared/layouts/Layout/Layout';

const TermsOfUse = () => {
  return (
    <Fragment>
      <NextSeo
        title="Terms of Use | Underline Events and Underline Digital Video Library"
        titleTemplate="%s"
        description="Terms of Use for Underline Science, including access to Underline Events, scientific conference services, hybrid events, virtual events, and the Underline Digital Video Library."
        openGraph={{
          description:
            'Terms of Use for Underline Science, including access to Underline Events, scientific conference services, hybrid events, virtual events, and the Underline Digital Video Library.',
          url: `${process.env.NEXT_PUBLIC_BASE_URL}/terms-of-use`,
        }}
      />
      <Layout>
        <Header>
          Underline Science, Inc.
          <br />
          Terms of Use
        </Header>
        <Content>
          <Text>
            <Text as="span" fontWeight="bold">
              Effective Date:{' '}
            </Text>
            May 1, 2026
            <br />
            <Text as="span" fontWeight="bold">
              Last Updated:{' '}
            </Text>
            April 28, 2026
          </Text>
          <Text my={8}>
            These Terms of Use govern your access to and use of the websites, platforms, event portals, video library,
            applications, digital services, event services, streaming services, conference tools, and related services
            operated by{' '}
            <Text as="span" fontWeight="bold">
              Underline Science, Inc.
            </Text>{' '}
            and its affiliates, representatives, contractors, and service providers.
          </Text>
          <Text as="span">These Terms apply to, among other services:</Text>
          <Text as="ol" listStyleType="decimal" ps={6}>
            <li>
              <Text as="span" fontWeight="bold">
                Underline Events
              </Text>
              , including in-person, virtual, and hybrid scientific conference services, event websites, registration
              tools, livestreaming, recordings, event schedules, attendee tools, speaker tools, exhibitor tools, help
              desk services, and conference-related digital services; and
            </li>
            <li>
              <Text as="span" fontWeight="bold">
                Underline Digital Video Library
              </Text>
              , including access to scientific lectures, academic videos, medical conference videos, research
              presentations, posters, slides, transcripts, abstracts, proceedings, metadata, DOI-linked content, MARC
              records, search tools, recommendation tools, and related discovery features.
            </li>
          </Text>
          <Text>
            By accessing or using the Services, creating an account, registering for an event, submitting content,
            presenting through an Underline-supported event, viewing library content, or otherwise using Underline, you
            agree to these Terms.
          </Text>
          <Text>If you do not agree to these Terms, do not use the Services.</Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            1. Definitions
          </Heading>
          <Text>
            <Text as="span" fontWeight="bold">
              &ldquo;Underline,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;
            </Text>{' '}
            means Underline Science, Inc.
          </Text>
          <Text>
            &ldquo;
            <Text as="span" fontWeight="bold">
              Services
            </Text>
            &rdquo; means all Underline websites, platforms, digital video library services, event services, conference
            portals, livestreaming tools, recording tools, search tools, analytics tools, event pages, support services,
            and related products.
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              &ldquo;Underline Events&rdquo;
            </Text>{' '}
            means Underline&apos;s event services and platform for scientific, academic, medical, professional society,
            hybrid, virtual, and in-person events.
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              &ldquo;Underline Digital Video Library&rdquo;
            </Text>{' '}
            means Underline&apos;s searchable digital library of scientific, academic, medical, technical, and
            professional conference content.
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              &ldquo;Content&rdquo;
            </Text>{' '}
            means videos, livestreams, recordings, lectures, presentations, slides, posters, abstracts, papers,
            proceedings, transcripts, translations, metadata, speaker profiles, images, text, files, comments, chat
            messages, Q&amp;A, event materials, and other materials available through or submitted to the Services.
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              &ldquo;User Content&rdquo;
            </Text>{' '}
            means Content submitted, uploaded, posted, presented, transmitted, or otherwise provided by users,
            presenters, speakers, authors, event organizers, societies, institutions, publishers, sponsors, exhibitors,
            or other third parties.
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              &ldquo;Authorized User&rdquo;
            </Text>{' '}
            means a user who is permitted to access specific Services or Content through an individual account, event
            registration, institutional subscription, library license, society agreement, publisher agreement, event
            organizer agreement, or other valid authorization.
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              &ldquo;Customer&rdquo;
            </Text>{' '}
            means an organization, institution, society, publisher, library, event organizer, sponsor, or other entity
            that contracts with Underline for Services.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            2. Relationship to Other Agreements
          </Heading>
          <Text>These Terms apply generally to use of the Services.</Text>
          <Text>
            Some Services may be subject to additional written agreements, order forms, statements of work, event
            contracts, speaker releases, institutional licenses, subscription agreements, sponsorship agreements, data
            processing agreements, or other terms. If there is a conflict between these Terms and a signed written
            agreement between Underline and a Customer, the signed written agreement controls for that Customer and the
            applicable Services.
          </Text>
          <Text>
            Event-specific rules, speaker instructions, attendee codes of conduct, exhibitor rules, registration terms,
            library license terms, and posted platform rules are incorporated into these Terms by reference.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            3. Eligibility and Account Responsibilities
          </Heading>
          <Text>
            You may use the Services only if you are legally able to agree to these Terms and are not prohibited from
            using the Services under applicable law.
          </Text>
          <Text>
            If you use the Services on behalf of an organization, institution, society, publisher, library, company, or
            other entity, you represent that you have authority to bind that entity to these Terms.
          </Text>
          <Text>
            You are responsible for maintaining the confidentiality of your account credentials and for all activity
            under your account. You may not share login credentials, bypass access controls, or allow unauthorized users
            to access restricted Content or Services.
          </Text>
          <Text>
            You must notify Underline promptly if you believe your account has been compromised or used without
            authorization.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            4. Access Rights and Limited License
          </Heading>
          <Text>
            Subject to these Terms and any applicable Customer agreement, Underline grants you a limited, revocable,
            non-exclusive, non-transferable right to access and use the Services for lawful, personal, educational,
            scholarly, research, professional, institutional, or event-related purposes.
          </Text>
          <Text>
            Access to specific Content or Services may depend on registration status, subscription status, institutional
            authorization, event access rights, geographic restrictions, payment status, license restrictions, speaker
            permissions, society permissions, publisher permissions, or other access controls.
          </Text>
          <Text>All rights not expressly granted are reserved by Underline and its licensors.</Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            5. Underline Events
          </Heading>
          <Text>
            Underline Events supports scientific conferences, academic events, medical meetings, research congresses,
            professional society events, technical conferences, virtual events, hybrid events, and in-person events.
          </Text>
          <Text>
            Depending on the specific event, Underline Events may include event websites, registration support,
            livestreaming, session recordings, hybrid event support, AV coordination, speaker tools, attendee tools,
            online schedules, exhibitor features, sponsor pages, help desk services, chat, Q&A, networking tools,
            digital posters, post-event access, and digital library publication.
          </Text>
          <Text>
            Event dates, schedules, speakers, sessions, formats, rooms, streaming availability, recordings, and access
            windows may change. Underline is not responsible for changes made by event organizers, societies, venues,
            speakers, sponsors, exhibitors, or other third parties.
          </Text>
          <Text>
            You agree to follow all event rules, platform instructions, speaker guidelines, attendee rules, and codes of
            conduct applicable to the event.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            6. Event Recordings, Livestreams, and Participation
          </Heading>
          <Text>
            Events supported by Underline may be livestreamed, recorded, photographed, transcribed, translated, indexed,
            summarized, archived, published, distributed, or made available after the event.
          </Text>
          <Text>
            By attending, speaking, presenting, moderating, participating in Q&A, appearing on camera, using chat,
            submitting questions, uploading materials, or otherwise participating in an Underline-supported event, you
            acknowledge that your name, image, likeness, voice, presentation, comments, questions, chat contributions,
            slides, poster, abstract, paper, or other submitted materials may be captured and used in connection with
            the event and related Services, subject to applicable event rules, speaker releases, Customer agreements,
            and applicable law.
          </Text>
          <Text>
            If you are a speaker, presenter, moderator, author, panelist, or contributor, you are responsible for
            ensuring that you have the rights, permissions, and consents necessary for your presentation and materials.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            7. Underline Digital Video Library
          </Heading>
          <Text>
            The Underline Digital Video Library provides access to scientific, academic, medical, technical, and
            professional conference content, including lectures, presentations, keynotes, panels, posters, slides,
            proceedings, transcripts, translations, metadata, abstracts, and related materials.
          </Text>
          <Text>
            Depending on your access rights, you may be able to search, browse, view, stream, cite, link to, download
            permitted files, save content, recommend content, or access associated materials.
          </Text>
          <Text>
            The availability of specific library Content may change over time due to rights restrictions, speaker
            requests, Customer instructions, publisher or society restrictions, legal issues, quality concerns,
            technical issues, or other reasons.
          </Text>
          <Text>
            Underline may withdraw, restrict, modify, correct, replace, or remove Content where it determines that such
            action is appropriate, including where Content may infringe rights, contain errors, violate these Terms,
            violate law, violate event rules, violate Customer agreements, or create reputational, legal, security, or
            operational risk.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            8. Permitted Uses of Library Content
          </Heading>
          <Text as="span">Unless a separate written agreement provides otherwise, Authorized Users may:</Text>
          <Text as="ol" listStyleType="decimal" ps={6}>
            <li>access, search, browse, and stream Content made available to them;</li>
            <li>cite Content using available citation tools, DOI links, URLs, or metadata;</li>
            <li>
              link to Content from course pages, library catalogs, learning management systems, institutional websites,
              research guides, or event websites, provided the link does not bypass access controls;
            </li>
            <li>
              download transcripts, slides, posters, papers, abstracts, or other materials only where a download
              function is expressly made available;
            </li>
            <li>
              use reasonable portions of permitted text materials for teaching, research, scholarship, citation,
              commentary, or classroom use, subject to applicable copyright law and license restrictions; and
            </li>
            <li>
              use Content for non-commercial educational, scholarly, research, instructional, or professional purposes
              within the scope of their authorization.
            </li>
          </Text>
          <Text>
            No rights are granted except those expressly stated in these Terms or a separate written agreement.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            9. Prohibited Uses
          </Heading>
          <Text as="span">You may not, and may not assist others to:</Text>
          <Text as="ol" listStyleType="decimal" ps={6}>
            <li>
              copy, download, scrape, crawl, harvest, rip, capture, reproduce, or store videos, streams, transcripts,
              metadata, slides, posters, abstracts, papers, or other Content except as expressly permitted;
            </li>
            <li>systematically download, extract, mirror, index, or build a database from the Services or Content;</li>
            <li>
              redistribute, sell, resell, sublicense, rent, lend, publish, broadcast, transmit, or commercially exploit
              the Services or Content;
            </li>
            <li>
              share passwords, access links, tokens, registration credentials, institutional credentials, or other
              access methods with unauthorized users;
            </li>
            <li>
              bypass, disable, circumvent, or interfere with access controls, DRM, watermarking, security controls,
              entitlement controls, authentication, authorization, geo-restrictions, or usage limits;
            </li>
            <li>
              remove or alter copyright notices, trademarks, attribution, disclaimers, speaker names, author names,
              watermarks, logos, metadata, DOI information, or proprietary notices;
            </li>
            <li>
              modify, edit, translate, adapt, create derivative works from, or alter Content unless expressly permitted;
            </li>
            <li>
              use the Services or Content to train, fine-tune, test, benchmark, or build artificial intelligence models,
              machine learning systems, search engines, competing databases, competing video platforms, or competing
              event platforms without Underline&apos;s prior written consent;
            </li>
            <li>
              reverse engineer, decompile, disassemble, copy, or attempt to derive source code, models, algorithms,
              workflows, data structures, or system architecture from the Services;
            </li>
            <li>use bots, scripts, automated tools, or excessive API calls except as expressly authorized;</li>
            <li>
              upload, submit, present, or transmit Content that infringes intellectual property rights, violates privacy
              or publicity rights, is defamatory, unlawful, misleading, harmful, obscene, abusive, discriminatory,
              harassing, or otherwise objectionable;
            </li>
            <li>
              interfere with livestreams, event sessions, chats, Q&A, help desks, event operations, library operations,
              platform security, or other users&apos; use of the Services;
            </li>
            <li>
              impersonate any person or organization or misrepresent your affiliation, identity, credentials,
              authorship, or authority;
            </li>
            <li>
              use the Services to send spam, malware, phishing content, harmful code, or unauthorized advertising;
            </li>
            <li>access the Services to build, benchmark, or improve a competing service; or</li>
            <li>
              use the Services in violation of applicable law, export controls, sanctions, institutional rules, event
              rules, or Customer agreements.
            </li>
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            10. User Content and Rights You Grant to Underline
          </Heading>
          <Text>
            You retain ownership of User Content you submit, upload, post, present, or otherwise provide, subject to any
            rights owned by your employer, institution, publisher, society, funder, co-authors, collaborators,
            licensors, or other third parties.
          </Text>
          <Text>
            By submitting, uploading, posting, presenting, transmitting, or otherwise providing User Content through the
            Services, you grant Underline a worldwide, non-exclusive, royalty-free, sublicensable, transferable license
            to host, store, copy, reproduce, encode, transcode, format, adapt for technical purposes, stream, display,
            perform, distribute, transmit, publish, index, search, excerpt, create thumbnails from, create metadata
            from, create transcripts from, create translations from, create summaries from, make accessible, preserve,
            archive, and otherwise use the User Content as reasonably necessary to provide, operate, improve, support,
            promote, secure, and make available the Services.
          </Text>
          <Text>
            This license includes the right to use User Content in connection with event pages, session pages, speaker
            pages, library records, search results, citations, DOI records, MARC records, metadata feeds, institutional
            access, customer reporting, technical support, and related platform features.
          </Text>
          <Text>
            For private, white-label, restricted-access, embargoed, or Customer-controlled Content, Underline&apos;s use
            of User Content will be subject to the applicable Customer agreement, event rules, or written instructions.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            11. Your Representations About User Content
          </Heading>
          <Text as="span">You represent and warrant that:</Text>
          <Text as="ol" listStyleType="decimal" ps={6}>
            <li>
              you own or have obtained all rights, licenses, permissions, releases, and consents necessary to submit and
              use your User Content through the Services;
            </li>
            <li>
              your User Content does not infringe copyrights, trademarks, trade secrets, moral rights, privacy rights,
              publicity rights, contractual rights, confidentiality obligations, or other rights of any person or
              entity;
            </li>
            <li>
              your User Content does not contain unlawful, defamatory, misleading, harmful, or otherwise objectionable
              material;
            </li>
            <li>
              you have obtained all required permissions for third-party images, charts, figures, videos, audio,
              datasets, text, publications, patient information, clinical information, copyrighted materials,
              confidential information, sponsor materials, or other protected materials included in your User Content;
            </li>
            <li>
              if your User Content includes co-authors, speakers, presenters, patients, students, research subjects,
              institutional materials, employer-owned materials, sponsor-owned materials, or publisher-owned materials,
              you have obtained all required approvals; and
            </li>
            <li>
              your submission and Underline&apos;s use of the User Content as permitted by these Terms will not violate
              applicable law or third-party rights.
            </li>
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            12. Speaker, Presenter, and Author Materials
          </Heading>
          <Text>
            Speakers, presenters, moderators, authors, panelists, and contributors are responsible for their own
            presentations, statements, claims, slides, posters, papers, abstracts, references, permissions, and
            supporting materials.
          </Text>
          <Text>
            Unless otherwise stated in a separate written agreement or speaker release, Underline does not claim
            ownership of speaker presentations, slides, posters, abstracts, papers, or research materials.
            Underline&apos;s role is to provide event, recording, hosting, streaming, publication, library, discovery,
            and related platform services.
          </Text>
          <Text>
            Speakers and presenters may be subject to additional event-specific consent forms, speaker releases,
            publication permissions, society policies, publisher policies, or Customer agreements.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            13. Institutional Access and Authorized Users
          </Heading>
          <Text>
            Institutions, libraries, societies, publishers, and other Customers may provide access to Authorized Users
            through IP authentication, SSO, domain access, registration lists, individual accounts, access codes, event
            credentials, or other approved methods.
          </Text>
          <Text>
            Customers are responsible for using reasonable efforts to ensure that only Authorized Users access
            restricted Services or Content. Customers must not knowingly permit unauthorized access, credential sharing,
            systematic downloading, redistribution, or other misuse.
          </Text>
          <Text>
            Underline may suspend, limit, or revoke access if it reasonably believes that access credentials are being
            misused, security controls are being bypassed, usage exceeds authorized limits, or Content is being accessed
            or used in violation of these Terms or an applicable agreement.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            14. Payments, Fees, Subscriptions, and Event Registration
          </Heading>
          <Text>
            Certain Services require payment, subscription, registration, license fees, event fees, platform fees,
            production fees, ingestion fees, hosting fees, or other charges.
          </Text>
          <Text>
            Fees, payment terms, cancellation rights, renewal terms, refund rules, and taxes may be stated in an order
            form, invoice, event registration page, subscription agreement, library license, statement of work, or other
            written agreement.
          </Text>
          <Text>
            Unless otherwise stated in a separate written agreement, all fees are non-refundable except where required
            by law or expressly approved by Underline.
          </Text>
          <Text>
            You are responsible for all taxes, duties, bank fees, credit card fees, chargebacks, currency conversion
            costs, and similar charges associated with your purchase or use of paid Services.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            15. Event Changes, Cancellations, and Refunds
          </Heading>
          <Text>
            Event organizers, venues, speakers, sponsors, societies, publishers, institutions, or other third parties
            may change, postpone, cancel, modify, or restrict events, sessions, speakers, formats, livestreams,
            recordings, access windows, or related services.
          </Text>
          <Text>
            Underline is not responsible for event changes, cancellations, postponements, travel costs, hotel costs,
            lost opportunities, or other losses caused by third-party event decisions, force majeure events, technical
            disruptions outside Underline&apos;s reasonable control, venue issues, speaker non-attendance, organizer
            decisions, or other circumstances beyond Underline&apos;s control.
          </Text>
          <Text>
            Refunds for event registration or Customer event services are governed by the applicable event registration
            terms, Customer agreement, invoice, or written event policy.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            16. AI-Powered Search, Recommendations, Summaries, and Transcripts
          </Heading>
          <Text>
            The Services may include AI-powered search, recommendation, transcript, translation, summarization,
            question-answering, citation, metadata, or discovery features.
          </Text>
          <Text>
            AI-generated or machine-generated outputs may be incomplete, inaccurate, outdated, or not reflect the full
            context of the underlying Content. You should verify important information against the original source
            Content and other authoritative sources.
          </Text>
          <Text>
            AI-generated outputs, search results, recommendations, transcripts, translations, and summaries are provided
            for research, discovery, accessibility, and convenience. They are not professional advice, medical advice,
            legal advice, clinical guidance, engineering advice, financial advice, or a substitute for independent
            expert judgment.
          </Text>
          <Text>
            You may not use AI features or outputs to bypass access restrictions, reconstruct restricted Content, create
            unauthorized derivative works, systematically extract Content, or build competing products.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            17. No Medical, Scientific, Legal, or Professional Advice
          </Heading>
          <Text>
            Content available through the Services may discuss scientific, medical, clinical, technical, legal,
            financial, policy, or professional topics. Such Content is provided for educational, scholarly, research,
            informational, or conference-related purposes only.
          </Text>
          <Text>
            Underline does not verify the accuracy, completeness, safety, efficacy, legality, scientific validity,
            clinical validity, or professional suitability of Content submitted by speakers, authors, presenters,
            societies, publishers, institutions, customers, sponsors, exhibitors, or other third parties.
          </Text>
          <Text>
            You are solely responsible for evaluating Content and obtaining appropriate expert advice before relying on
            it.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            18. Intellectual Property Rights
          </Heading>
          <Text>
            The Services, including software, platform design, interfaces, workflows, databases, search functionality,
            metadata structures, graphics, logos, trademarks, service marks, documentation, event platform features,
            library features, and other proprietary elements, are owned by Underline or its licensors and are protected
            by intellectual property laws.
          </Text>
          <Text>
            You may not use Underline&apos;s name, trademarks, logos, branding, domain names, product names, or other
            brand features without Underline&apos;s prior written consent.
          </Text>
          <Text>
            All third-party Content, trademarks, logos, names, and materials remain the property of their respective
            owners.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            19. Copyright Complaints and Takedown Requests
          </Heading>
          <Text>Underline respects intellectual property rights.</Text>
          <Text>
            If you believe Content available through the Services infringes your copyright, you may submit a written
            notice to Underline at:
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              Underline Science, Inc.
            </Text>
            <br />
            1216 Broadway, 2nd Floor
            <br />
            New York, NY 10001
            <br />
            United States
            <br />
            <Text my={4}>
              Email: support@underline.io
              <br />
              Subject line: Copyright Notice
            </Text>
          </Text>
          <Text as="span">Your notice should include:</Text>
          <Text as="ol" listStyleType="decimal" ps={6}>
            <li>your physical or electronic signature;</li>
            <li>identification of the copyrighted work you claim has been infringed;</li>
            <li>
              identification of the allegedly infringing material and information reasonably sufficient to locate it;
            </li>
            <li>your name, address, telephone number, and email address;</li>
            <li>
              a statement that you have a good-faith belief that use of the material in the manner complained of is not
              authorized by the copyright owner, its agent, or the law; and
            </li>
            <li>
              a statement, made under penalty of perjury, that the information in the notification is accurate and that
              you are the copyright owner or authorized to act on behalf of the copyright owner.
            </li>
          </Text>
        </Content>
      </Layout>
    </Fragment>
  );
};

export default TermsOfUse;
